import {
  TabItem,
  Tabs,
  useModals,
  Modal,
  ModalProps,
  Button,
  Input,
  Multiselect,
  Select,
  Stack,
  Switch,
  Title,
} from "@skailer-com/uikit";
import { InputWithVariableInsert } from "@/components/input-with-variable-insert/InputWithVariableInsert.tsx";
import { DemoUnavailable } from "@/components/demo-unavailable/DemoUnavailable.tsx";
import { RichEditor } from "@/components/rich-editor/RichEditor.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { useWorkflowPlaceholderOptions } from "@/pages/workflows/edit/actions/use-workflow-placeholder-options.ts";
import { WorkflowActionTimingEditor } from "@/pages/workflows/edit/actions/WorkflowActionTimingEditor.tsx";
import {
  getWorkflowActionTimingPayload,
  getWorkflowActionTimingValues,
  useWorkflows,
} from "@/pages/workflows/workflow-hook.tsx";
import { useWorkflowBlockConditions } from "@/pages/workflows/edit/flow/use-workflow-block-conditions.tsx";
import { WorkflowBlockConditionsList } from "@/pages/workflows/edit/flow/WorkflowBlockConditionsList.tsx";
import useEmployeeSearchInline from "@/queries/employees/use-employees.query.ts";
import { useSlackChannels } from "@/queries/integrations/use-integrations.query.ts";
import { useEmployeeReferenceCustomFields } from "@/queries/use-custom-fields.query.ts";
import { useWorkflowUpdateBlockActionNotification } from "@/queries/workflows/workflow.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { EmployeeFilterDefault } from "@/types/employees/employee.types.ts";
import {
  ApiNotificationChannelType,
  getApiNotificationChannelTypeTitle,
} from "@/types/notification.types.ts";
import {
  ApiWorkflowBlockActionNotification,
  ApiWorkflowBlockActionNotificationRecipientTypes,
  ApiWorkflowBlockActionNotificationSave,
  getApiWorkflowBlockActionNotificationRecipientTypesSelectItems,
} from "@/types/workflows/workflow.action-notification.types.ts";
import { ApiWorkflowBlockActionTypes } from "@/types/workflows/workflow.base-enum.types.ts";
import { ApiWorkflowBlock, ApiWorkflowVersion } from "@/types/workflows/workflow.types.ts";
import { yupResolver } from "@hookform/resolvers/yup";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

export interface WorkflowEditActionSendNotificationModalProps extends ModalProps {
  workflowVersion: ApiWorkflowVersion;
  block: ApiWorkflowBlock;
  actionNotification: ApiWorkflowBlockActionNotification;
}

export const WorkflowEditActionSendNotificationModal = ({
  workflowVersion,
  block,
  actionNotification,
  ...props
}: WorkflowEditActionSendNotificationModalProps) => {
  const { close } = useModals();
  const { id } = { ...props };
  const { t } = useTranslation();
  const { workflowActionTimingSchemaFields } = useWorkflows();
  const schemaObject = yup.object({
    title: yup.string().defined().nullable(),
    ...workflowActionTimingSchemaFields,
    subject: yup.string().required(t("errors:field_required")),
    body: yup.string().required(t("errors:field_required")),
    recipient_type: yup
      .mixed<ApiWorkflowBlockActionNotificationRecipientTypes>()
      .oneOf(Object.values(ApiWorkflowBlockActionNotificationRecipientTypes))
      .required(),
    recipient_value: yup
      .array()
      .of(yup.string().required())
      .when("recipient_type", {
        is: ApiWorkflowBlockActionNotificationRecipientTypes.slack_channels,
        then: (schema) =>
          schema.min(1, t("errors:field_required")).required(t("errors:field_required")),
      })
      .required(),
    to_channels: yup
      .array()
      .of(
        yup
          .mixed<ApiNotificationChannelType>()
          .oneOf(Object.values(ApiNotificationChannelType))
          .required()
      )
      .when("recipient_type", {
        is: ApiWorkflowBlockActionNotificationRecipientTypes.slack_channels,
        then: (schema) => schema.required(),
        otherwise: (schema) =>
          schema.min(1, t("errors:field_required")).required(t("errors:field_required")),
      })
      .required(),
    skip_notification_to_affected_employee: yup.boolean().required(t("errors:field_required")),
  });

  const { conditions, handleAddCondition, handleEditCondition, handleDeleteCondition } =
    useWorkflowBlockConditions(workflowVersion, block);

  const form = useForm<ApiWorkflowBlockActionNotificationSave>({
    mode: "onSubmit",
    resolver: yupResolver(schemaObject),
    values: {
      ...getWorkflowActionTimingValues(block),
      title: block.title ?? "",
      subject: actionNotification.subject,
      body: actionNotification.body,
      recipient_type: actionNotification.recipient_type,
      recipient_value: actionNotification.recipient_value,
      to_channels: actionNotification.to_channels,
      skip_notification_to_affected_employee:
        actionNotification.skip_notification_to_affected_employee,
    },
  });
  const {
    control,
    handleSubmit,
    register,
    watch,
    formState: { errors },
  } = form;

  const eventType = workflowVersion.trigger_event?.event_type;
  const placeholderOptions = useWorkflowPlaceholderOptions(eventType, ["employee", "manager"]);

  const updateBlockActionNotification = useWorkflowUpdateBlockActionNotification(control);

  const recipientType = watch("recipient_type");
  const toChannels = watch("to_channels");
  const usesSlackDelivery =
    recipientType === ApiWorkflowBlockActionNotificationRecipientTypes.slack_channels ||
    toChannels.includes(ApiNotificationChannelType.slack);
  const usesEmailDelivery =
    recipientType !== ApiWorkflowBlockActionNotificationRecipientTypes.slack_channels &&
    toChannels.includes(ApiNotificationChannelType.email);
  const employeesSearch = useEmployeeSearchInline(
    recipientType == ApiWorkflowBlockActionNotificationRecipientTypes.specific_employee,
    {
      ...EmployeeFilterDefault,
    }
  );
  const employeeCustomFields = useEmployeeReferenceCustomFields(
    recipientType ===
      ApiWorkflowBlockActionNotificationRecipientTypes.custom_field_employee_reference ||
      recipientType ===
        ApiWorkflowBlockActionNotificationRecipientTypes.custom_field_employee_reference_subordinates
  );

  const { data: slackChannels } = useSlackChannels();

  const toChannelsOptions = [
    {
      id: ApiNotificationChannelType.email,
      title: getApiNotificationChannelTypeTitle(ApiNotificationChannelType.email),
    },
    {
      id: ApiNotificationChannelType.slack,
      title: getApiNotificationChannelTypeTitle(ApiNotificationChannelType.slack),
    },
    {
      id: ApiNotificationChannelType.messenger,
      title: getApiNotificationChannelTypeTitle(ApiNotificationChannelType.messenger),
    },
  ];

  const handleSave = handleSubmit(async (data) => {
    try {
      const blockData =
        data.recipient_type === ApiWorkflowBlockActionNotificationRecipientTypes.slack_channels
          ? { ...data, to_channels: [] }
          : {
              ...data,
              to_channels: data.to_channels.filter((value) =>
                toChannelsOptions.some((option) => option.id === value)
              ),
            };

      await updateBlockActionNotification.mutateAsync({
        workflowId: workflowVersion.workflow_id,
        workflowVersionId: workflowVersion.id,
        workflowBlockId: block.id,
        payload: {
          title: data.title,
          ...getWorkflowActionTimingPayload(data),
          block_data: blockData,
          conditions: conditions.map((x) => ({ ...x })),
        },
      });

      analyticsService.trackEvent(analyticEvents.workflows.blockEdited, {
        [analyticProperties.id]: workflowVersion.workflow_id,
        [analyticProperties.actionType]: ApiWorkflowBlockActionTypes.send_notification,
      });

      close(id);
    } catch {
      // handle error
    }
  });

  const handleTabChanged = (tabIndex: number) => {
    setActiveTab(tabIndex);
  };

  const [activeTab, setActiveTab] = useState(0);

  return (
    <>
      <Modal
        {...props}
        variant={"side"}
        title={t("workflow:action_type_notification")}
        layout={"stretch"}
        withCloser
        closeByEscEnabled
        size={"lg"}
        actions={
          <Stack gap={"sm"}>
            <ServerErrorField errors={errors} />
            <Button
              size={"lg"}
              className={"w-full"}
              type={"submit"}
              onClick={handleSave}
              isLoading={updateBlockActionNotification.isPending}
            >
              {t("common:save")}
            </Button>
          </Stack>
        }
      >
        <form onSubmit={handleSave}>
          <Tabs onActiveTabChange={handleTabChanged} activeTab={activeTab}>
            <TabItem title={t("common:message")}>
              <Stack gap={"lg"} className={"py-6"}>
                <Stack>
                  <Controller
                    render={({ field: { value, onChange } }) => (
                      <Select
                        label={t("workflow:action_type_notification_recipient_type")}
                        emptyTitle={t("common:select")}
                        value={value}
                        required={true}
                        options={getApiWorkflowBlockActionNotificationRecipientTypesSelectItems(
                          eventType
                        )}
                        onChange={(newType) => {
                          onChange(newType);
                          form.setValue("recipient_value", []);
                        }}
                        error={errors.recipient_type?.message}
                      />
                    )}
                    control={control}
                    name={"recipient_type"}
                  />
                  {recipientType ==
                    ApiWorkflowBlockActionNotificationRecipientTypes.specific_employee && (
                    <Controller
                      render={({ field: { value, onChange } }) => (
                        <Multiselect
                          label={t("core:employee")}
                          emptyTitle={t("common:select")}
                          isLoading={employeesSearch.isLoading}
                          showCaption={true}
                          showAvatar={true}
                          values={value ?? []}
                          searchable={true}
                          isClearable={false}
                          required={true}
                          options={actionNotification.recipient_entities}
                          onSearchOptions={(text) => employeesSearch.search(text)}
                          searchOptions={employeesSearch.options}
                          loadOptionsOnOpen
                          onChange={onChange}
                          error={errors.recipient_value?.message}
                        />
                      )}
                      control={control}
                      name={"recipient_value"}
                    />
                  )}
                  {[
                    ApiWorkflowBlockActionNotificationRecipientTypes.custom_field_employee_reference,
                    ApiWorkflowBlockActionNotificationRecipientTypes.custom_field_employee_reference_subordinates,
                  ].includes(recipientType) && (
                    <Controller
                      render={({ field: { value, onChange } }) => (
                        <Select
                          label={t("core:rule_custom_field_employee_reference")}
                          emptyTitle={t("common:select")}
                          isLoading={employeeCustomFields.isLoading}
                          value={value ? value[0] : undefined}
                          searchable={true}
                          isClearable={false}
                          required={true}
                          options={employeeCustomFields.data}
                          onChange={(e) => {
                            onChange([e]);
                          }}
                          error={errors.recipient_value?.message}
                        />
                      )}
                      control={control}
                      name={"recipient_value"}
                    />
                  )}

                  {recipientType ==
                    ApiWorkflowBlockActionNotificationRecipientTypes.slack_channels && (
                    <Controller
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <Multiselect
                          values={value ?? []}
                          searchable={true}
                          options={slackChannels?.map((channel) => ({
                            id: channel.channel_id,
                            title: channel.name,
                            icon: "fa-brands fa-slack",
                          }))}
                          onChange={onChange}
                          required={true}
                          error={errors.recipient_value?.message}
                          label={t("blog:slack_channel")}
                          emptyTitle={""}
                        />
                      )}
                      name={"recipient_value"}
                    />
                  )}

                  {recipientType ==
                    ApiWorkflowBlockActionNotificationRecipientTypes.external_emails && (
                    <Controller
                      render={({ field: { value, onChange } }) => (
                        <Input
                          label={t("core:email")}
                          value={Array.isArray(value) ? value.join(", ") : ""}
                          onChange={(e) => {
                            const emails = e.target.value
                              .split(",")
                              .map((email) => email.trim())
                              .filter((email) => email.length > 0);
                            onChange(emails);
                          }}
                          error={errors.recipient_value?.message}
                        />
                      )}
                      control={control}
                      name={"recipient_value"}
                    />
                  )}
                  {recipientType !==
                    ApiWorkflowBlockActionNotificationRecipientTypes.slack_channels && (
                    <Controller
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <Multiselect
                          values={value}
                          onChange={onChange}
                          label={t("workflow:action_type_notification_to_channels")}
                          emptyTitle={""}
                          required={true}
                          error={errors.to_channels?.message}
                          options={toChannelsOptions}
                        />
                      )}
                      name={"to_channels"}
                    />
                  )}
                  {usesSlackDelivery && (
                    <DemoUnavailable
                      appearance="notice"
                      label={t("integrations:demo_slack_notifications_title")}
                      description={t("integrations:demo_slack_notifications_description")}
                      className={"mt-3"}
                    />
                  )}
                  {usesEmailDelivery && (
                    <DemoUnavailable
                      appearance="notice"
                      label={t("integrations:demo_email_delivery_title")}
                      description={t("integrations:demo_email_delivery_description")}
                      className={"mt-2"}
                    />
                  )}
                </Stack>

                <Stack gap={"lg"}>
                  <Title header={t("workflow:action_type_notification_message_text")} />
                  <InputWithVariableInsert
                    label={t("core:email_subject")}
                    type="text"
                    {...register("subject")}
                    required={true}
                    error={errors.subject?.message}
                    variableOptions={placeholderOptions}
                  />
                  <Stack>
                    <Title
                      header={t("workflow:action_type_notification_body")}
                      size={"xs"}
                      paddingBottom={true}
                      required={true}
                    />
                    <Controller
                      control={control}
                      name={"body"}
                      render={({ field: { value, onChange } }) => (
                        <RichEditor
                          value={value ?? ""}
                          onChange={onChange}
                          error={errors.body?.message}
                          placeholderOptions={placeholderOptions}
                        />
                      )}
                    />
                  </Stack>
                </Stack>
              </Stack>
            </TabItem>
            <TabItem title={t("workflow:conditions")}>
              <Stack gap={"lg"} className={"py-6"}>
                <Stack>
                  <Title header={t("workflow:block")} size={"xs"} />
                  <Input
                    label={t("common:title")}
                    type="text"
                    {...register("title")}
                    error={errors.title?.message}
                  />
                </Stack>
                <WorkflowActionTimingEditor
                  control={control}
                  register={register}
                  errors={errors}
                  triggerDateAvailable={workflowVersion.trigger_schedule_rule !== null}
                />
                <Controller
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <Switch
                      checked={value}
                      onChange={onChange}
                      title={t(
                        "workflow:action_type_notification_skip_notification_to_affected_employee"
                      )}
                      description={t(
                        "workflow:action_type_notification_skip_notification_to_affected_employee_description"
                      )}
                    />
                  )}
                  name={"skip_notification_to_affected_employee"}
                />
                <WorkflowBlockConditionsList
                  conditions={conditions}
                  onAdd={handleAddCondition}
                  onEdit={handleEditCondition}
                  onDelete={handleDeleteCondition}
                />
              </Stack>
            </TabItem>
          </Tabs>
        </form>
      </Modal>
    </>
  );
};
